/**
 * 타임존 시각 포맷 + 업무일/쉬는날 판별.
 * 시각: 브라우저 Intl (API 불필요)
 * 공휴일: Nager.Date 공개 API (무료, 키 불필요)
 */

export type ZoneLabel = {
  id: string;
  name: string;
  timeZone: string;
  abbr: string;
  /** ISO 3166-1 alpha-2 for Nager.Date */
  countryCode: string;
};

export const REGION_TIME_ZONES: ZoneLabel[] = [
  {
    id: "kr",
    name: "한국",
    timeZone: "Asia/Seoul",
    abbr: "KST",
    countryCode: "KR",
  },
  {
    id: "cn",
    name: "중국",
    timeZone: "Asia/Shanghai",
    abbr: "CST",
    countryCode: "CN",
  },
  {
    id: "jp",
    name: "일본",
    timeZone: "Asia/Tokyo",
    abbr: "JST",
    countryCode: "JP",
  },
];

export type DayKind = "WORKDAY" | "WEEKEND" | "HOLIDAY";

export interface RegionDayInfo {
  id: string;
  name: string;
  abbr: string;
  timeZone: string;
  /** 2026.08.05 16:43:07 */
  dateTime: string;
  /** 업무일 | 주말 | 공휴일 */
  dayKind: DayKind;
  dayLabel: string;
  holidayName?: string;
}

/** 예: 2026.08.05 16:43 */
export function formatZonedYmdHm(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "00";

  return `${get("year")}.${get("month")}.${get("day")} ${get("hour")}:${get(
    "minute"
  )}`;
}

/** 예: 2026.08.05 16:43:07 */
export function formatZonedYmdHms(
  date: Date,
  timeZone: string,
  abbr?: string
): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "00";

  const base = `${get("year")}.${get("month")}.${get("day")} ${get("hour")}:${get(
    "minute"
  )}:${get("second")}`;
  return abbr ? `${base} ${abbr}` : base;
}

/** 해당 타임존의 달력 날짜 YYYY-MM-DD */
export function getZonedYmd(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** 0=일 … 6=토 (해당 타임존 기준) */
export function getZonedWeekday(date: Date, timeZone: string): number {
  const wd = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
  }).format(date);
  const map: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return map[wd] ?? 0;
}

interface NagerHoliday {
  date: string;
  localName?: string;
  name?: string;
}

const holidayCache = new Map<string, Map<string, string>>();

/** Nager.Date 연도별 공휴일 로드 (kr/cn) */
export async function loadCountryHolidays(
  countryCode: string,
  year: number
): Promise<Map<string, string>> {
  const key = `${countryCode}-${year}`;
  const cached = holidayCache.get(key);
  if (cached) return cached;

  const map = new Map<string, string>();
  try {
    const res = await fetch(
      `https://date.nager.at/api/v3/PublicHolidays/${year}/${countryCode}`
    );
    if (res.ok) {
      const list = (await res.json()) as NagerHoliday[];
      for (const h of list) {
        if (h.date) {
          map.set(h.date, h.localName || h.name || "공휴일");
        }
      }
    }
  } catch {
    // 네트워크 실패 시 주말만으로 판별
  }
  holidayCache.set(key, map);
  return map;
}

export function resolveDayStatus(
  date: Date,
  timeZone: string,
  holidays: Map<string, string>
): { dayKind: DayKind; dayLabel: string; holidayName?: string } {
  const ymd = getZonedYmd(date, timeZone);
  const holidayName = holidays.get(ymd);
  if (holidayName) {
    return { dayKind: "HOLIDAY", dayLabel: "공휴일", holidayName };
  }
  const wd = getZonedWeekday(date, timeZone);
  if (wd === 0 || wd === 6) {
    return { dayKind: "WEEKEND", dayLabel: "주말" };
  }
  return { dayKind: "WORKDAY", dayLabel: "업무일" };
}

/** 한국·중국·일본 시각 + 업무일 여부 */
export async function buildRegionDayInfos(
  date: Date = new Date()
): Promise<RegionDayInfo[]> {
  return Promise.all(
    REGION_TIME_ZONES.map(async (z) => {
      const ymd = getZonedYmd(date, z.timeZone);
      const year = Number(ymd.slice(0, 4));
      const holidays = await loadCountryHolidays(z.countryCode, year);
      const status = resolveDayStatus(date, z.timeZone, holidays);
      return {
        id: z.id,
        name: z.name,
        abbr: z.abbr,
        timeZone: z.timeZone,
        dateTime: formatZonedYmdHms(date, z.timeZone),
        dayKind: status.dayKind,
        dayLabel: status.dayLabel,
        holidayName: status.holidayName,
      };
    })
  );
}
